// Firebase Auth, loaded on demand.
//
// The Firebase client SDK (app + auth: ~130 KB raw, ~39 KB gzip) used to be
// imported statically by AuthProvider and ~90 components, so every page
// downloaded and parsed it before it could become interactive. Nothing may
// import "firebase/auth" statically any more (eslint enforces it; type-only
// imports are fine): the SDK is fetched the first time something needs it -
// a returning visitor's session check right after hydration, the sign-in
// modal, or an authed request - and every caller shares the one instance.
// A visitor with no stored session never downloads it until they go to sign
// in (see hasStoredSession).
//
// For Authorization headers use getIdToken() / requireIdToken() rather than
// getAuth().currentUser.getIdToken(). They also wait for Firebase to restore
// the session, so a request made while the page is still loading no longer
// goes out without a token (currentUser is null until that restore ends).
import type { Auth, User } from "firebase/auth";
import { setIdTokenGetter } from "@jammable/clientapi/src/shared";

export type FirebaseAuthModule = typeof import("firebase/auth");

let apiPromise: Promise<{ auth: Auth; fb: FirebaseAuthModule }> | null = null;
let loadedAuth: Auth | null = null;
let stateReady = false;
const waiting = new Set<(auth: Auth) => void>();

/** Loads the SDK (once) and returns the Auth instance plus the module, for
 * calling sign-in functions: `const { auth, fb } = await loadAuthApi();
 * fb.signInWithPopup(auth, new fb.GoogleAuthProvider())`. Client only. */
export function loadAuthApi(): Promise<{ auth: Auth; fb: FirebaseAuthModule }> {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Firebase Auth is client-only"));
  }
  if (!apiPromise) {
    apiPromise = Promise.all([import("firebase/auth"), import("./firebase")])
      .then(([fb, app]) => {
        const auth = fb.getAuth(app.default);
        loadedAuth = auth;
        auth
          .authStateReady()
          .then(() => {
            stateReady = true;
          })
          .catch(() => {});
        waiting.forEach((cb) => cb(auth));
        waiting.clear();
        return { auth, fb };
      })
      .catch((err) => {
        // A failed chunk fetch (flaky network, a deploy swapping chunks)
        // must not poison every later sign-in or authed request.
        apiPromise = null;
        throw err;
      });
  }
  return apiPromise;
}

export function loadAuth(): Promise<Auth> {
  return loadAuthApi().then(({ auth }) => auth);
}

/** The Auth instance if something has already loaded it; never loads it. */
export function getLoadedAuth(): Auth | null {
  return loadedAuth;
}

/** Calls `cb` with the Auth instance as soon as anything loads it (now, if
 * it already has), without triggering the load. Returns a canceller. */
export function whenAuthLoaded(cb: (auth: Auth) => void): () => void {
  if (loadedAuth) {
    cb(loadedAuth);
    return () => {};
  }
  waiting.add(cb);
  return () => {
    waiting.delete(cb);
  };
}

// Firebase persists the signed-in user under this key prefix
// ("firebase:authUser:<apiKey>:<app>"), in IndexedDB by default, else
// localStorage or sessionStorage.
const AUTH_USER_KEY = "firebase:authUser:";

function storageHasAuthUser(storage: Storage): boolean {
  for (let i = 0; i < storage.length; i++) {
    if (storage.key(i)?.startsWith(AUTH_USER_KEY)) return true;
  }
  return false;
}

// Reads Firebase's IndexedDB store without loading the SDK. Resolves true
// when unsure, so a doubt costs a download, never a signed-in user.
function indexedDbHasAuthUser(): Promise<boolean> {
  return new Promise((resolve) => {
    let settled = false;
    const done = (value: boolean) => {
      if (!settled) {
        settled = true;
        resolve(value);
      }
    };
    let req: IDBOpenDBRequest;
    try {
      req = indexedDB.open("firebaseLocalStorageDb");
    } catch {
      return done(true);
    }
    // No such database: Firebase has never stored a user here. Abort so
    // this check doesn't create an empty one.
    req.onupgradeneeded = () => {
      done(false);
      req.transaction?.abort();
    };
    req.onerror = () => done(true);
    req.onblocked = () => done(true);
    req.onsuccess = () => {
      const db = req.result;
      try {
        if (!db.objectStoreNames.contains("firebaseLocalStorage")) {
          db.close();
          return done(false);
        }
        const keys = db
          .transaction("firebaseLocalStorage", "readonly")
          .objectStore("firebaseLocalStorage")
          .getAllKeys();
        keys.onsuccess = () => {
          done(keys.result.some((k) => String(k).startsWith(AUTH_USER_KEY)));
          db.close();
        };
        keys.onerror = () => {
          done(true);
          db.close();
        };
      } catch {
        db.close();
        done(true);
      }
    };
  });
}

/** Whether this browser may hold a Firebase session, answered without
 * loading the SDK: our token cookie or cached profile, or a user in
 * Firebase's own storage. Signed-out visitors (most search traffic) get
 * false, so nothing downloads Firebase for them until they go to sign in. */
export async function hasStoredSession(): Promise<boolean> {
  if (typeof window === "undefined") return false;
  if (apiPromise) return true;
  if (/(?:^|;\s*)token=[^;]+/.test(document.cookie)) return true;
  try {
    if (localStorage.getItem("user")) return true;
    if (storageHasAuthUser(localStorage)) return true;
    if (storageHasAuthUser(sessionStorage)) return true;
  } catch {
    // Storage blocked: Firebase can only be using IndexedDB, or nothing.
  }
  if (typeof indexedDB === "undefined") return false;
  return indexedDbHasAuthUser();
}

/** The signed-in Firebase user, after Firebase has restored the session.
 * null when signed out, and always null on the server. When nothing in
 * this browser holds a session it answers null without loading the SDK:
 * the public reads that attach a token if there is one (comments, a
 * collection's voices, packages/ui's getAuthHeaders) used to download
 * Firebase for every signed-out visitor right after hydration. */
export async function getCurrentUser(): Promise<User | null> {
  if (typeof window === "undefined") return null;
  if (!(await hasStoredSession())) return null;
  const auth = await loadAuth();
  await auth.authStateReady();
  return auth.currentUser;
}

/** ID token for an Authorization header, or null when signed out. */
export async function getIdToken(forceRefresh = false): Promise<string | null> {
  const user = await getCurrentUser();
  return user ? user.getIdToken(forceRefresh) : null;
}

/** ID token, or throws when signed out - for callers that used
 * `auth.currentUser.getIdToken()` and relied on it throwing. */
export async function requireIdToken(): Promise<string> {
  const user = await getCurrentUser();
  if (!user) throw new Error("User not authenticated.");
  return user.getIdToken();
}

/** Synchronous best answer to "is someone signed in?": Firebase's, once it
 * has loaded and restored the session; until then `fallback`, which should
 * be AuthProvider's user (the cached profile on a return visit). */
export function isSignedIn(fallback: unknown): boolean {
  if (loadedAuth && stateReady) return !!loadedAuth.currentUser;
  return !!fallback;
}

/** auth.onAuthStateChanged, loading the SDK first - or, with
 * `{ load: false }`, subscribing only once something else has loaded it
 * (nobody can sign in before that). Returns an unsubscriber that is safe to
 * call before the SDK has arrived. */
export function onAuthStateChanged(
  cb: (user: User | null) => void,
  { load = true }: { load?: boolean } = {},
): () => void {
  let unsubscribe: (() => void) | null = null;
  let cancelled = false;
  const subscribe = (auth: Auth) => {
    if (!cancelled) unsubscribe = auth.onAuthStateChanged(cb);
  };
  let stopWaiting = () => {};
  if (load) loadAuth().then(subscribe, () => {});
  else stopWaiting = whenAuthLoaded(subscribe);
  return () => {
    cancelled = true;
    stopWaiting();
    unsubscribe?.();
  };
}

export async function signOut(): Promise<void> {
  const auth = await loadAuth();
  await auth.signOut();
}

// packages/ui's API helpers (getAuthHeaders) build their headers from this.
setIdTokenGetter(() => getIdToken());
